import React, { useEffect, useState } from "react";
import axios from "axios";
import { API_BASE_URL } from "../config";
import { useAuth } from '../components/shared/AuthContext';
import { LogoLoader } from "../components/ui";

/* ── Jothilinga Design Tokens ── */
const J = {
  primary:    "#3F51B5",   // Indigo  (sidebar icons)
  primaryDk:  "#303F9F",   // Darker indigo
  accent:     "#2196F3",   // Bright blue
  light:      "#c3dbeb",   // Sidebar bg
  lightBg:    "#EEF2FB",   // Page background tint
  success:    "#27ae60",
  danger:     "#e74c3c",
  text:       "#1a237e",   // Deep navy for headings
};

const BASE_GROUP_OPTIONS = ["ASSET", "LIABILITY", "INCOME", "EXPENSE", "EQUITY"];

const AccountGroup = () => {
  const { data, roleName } = useAuth();
  const branchId     = data?.branchId || null;
  const isSuperAdmin = roleName === "superadmin";

  const [accountGroups, setAccountGroups] = useState([]);
  const [loading,       setLoading]       = useState(false);
  const [showModal,     setShowModal]     = useState(false);
  const [editingItem,   setEditingItem]   = useState(null);
  const [formData,      setFormData]      = useState({ base_group: "", account_group: "" });
  const [search,        setSearch]        = useState("");
  const [rowsPerPage,   setRowsPerPage]   = useState(5);
  const [currentPage,   setCurrentPage]   = useState(1);

  useEffect(() => { loadAccountGroups(); }, []);

  // Reads base_ledger_group, NOT account_group.
  //
  // The app carries two parallel grouping structures. `account_group` ->
  // `ledger_group` was built but never populated (0 rows since creation), while
  // `base_ledger_group` -> `ledger_account` is the one actually wired to
  // ledger_transaction and therefore to the day-end. This page now shows the
  // live structure rather than an empty one.
  //
  // Field mapping: group_name -> account_group, group_type -> base_group.
  const loadAccountGroups = async () => {
    setLoading(true);
    try {
      const res = await axios.get("/api/getModelData/base_ledger_group", { baseURL: API_BASE_URL, params: { filter: "{}" } });
      const list = Array.isArray(res.data?.data) ? res.data.data : Array.isArray(res.data) ? res.data : [];
      // Present base_ledger_group rows in the shape this page renders.
      setAccountGroups(
        list.map((g) => ({
          id: g.id,
          account_group: g.group_name,
          base_group: String(g.group_type || "").toUpperCase(),
          is_default: g.status ? 1 : 0,
          _raw: g,
        }))
      );
    } catch (err) { console.error(err); setAccountGroups([]); }
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.base_group || !formData.account_group.trim()) return;
    setLoading(true);
    try {
      const payload = {
        type: editingItem ? "Edit" : "Add",
        model: "base_ledger_group",
        group_name: formData.account_group.trim(),
        group_type: formData.base_group,
        status: 1,
      };
      if (editingItem) payload.id = editingItem.id;
      await axios.post("/api/addDetails", payload, { baseURL: API_BASE_URL });
      await loadAccountGroups();
      closeModal();
    } catch { alert("Error saving. Please try again."); }
    setLoading(false);
  };

  const openAdd  = () => { setEditingItem(null); setFormData({ base_group: "", account_group: "" }); setShowModal(true); };
  const openEdit = (item) => { setEditingItem(item); setFormData({ base_group: item.base_group || "", account_group: item.account_group || "" }); setShowModal(true); };
  const closeModal = () => { setShowModal(false); setEditingItem(null); setFormData({ base_group: "", account_group: "" }); };

  const filtered   = accountGroups.filter(g => (g.account_group || "").toLowerCase().includes(search.toLowerCase()) || (g.base_group || "").toLowerCase().includes(search.toLowerCase()));
  const totalPages = Math.max(1, Math.ceil(filtered.length / rowsPerPage));
  const paginated  = filtered.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

  const baseGroupColor = { ASSET: "#27ae60", LIABILITY: "#e74c3c", INCOME: "#2196F3", EXPENSE: "#e67e22", EQUITY: "#8e44ad" };

  return (
    <div style={{ background: "transparent", minHeight: "100vh" }}>
      {/* ── Page Header ── */}
      <div style={{ background: "transparent", padding: "4px 0", marginBottom: 20 }}>
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
          <div>
            <h5 className="mb-0 fw-bold" style={{ color: "#004BB5", fontSize: "1.15rem" }}>
              <i className="fas fa-layer-group me-2" />Account Groups
            </h5>
          </div>
          <div className="d-flex gap-2 align-items-center" style={{ flexWrap: "nowrap" }}>
            <input
              type="text"
              placeholder="Search..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setCurrentPage(1);
              }}
              style={{
                borderRadius: 8,
                border: "1px solid #CBD5E1",
                background: "#fff",
                color: "#1E293B",
                padding: "4px 12px",
                height: 32,
                outline: "none",
                width: 220,
                fontSize: "0.84rem",
              }}
            />
            <button
              onClick={openAdd}
              style={{
                background: "#004BB5",
                color: "#fff",
                border: "none",
                borderRadius: 8,
                padding: "0 16px",
                height: 32,
                fontWeight: 600,
                fontSize: "0.84rem",
                cursor: "pointer",
                whiteSpace: "nowrap",
                flexShrink: 0,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              + Add
            </button>
          </div>
        </div>
      </div>

      {/* ── Table Card ── */}
      <div style={{ background: "#fff", borderRadius: 12, boxShadow: "0 2px 12px rgba(0,0,0,0.07)", overflow: "hidden" }}>
        <div className="table-responsive">
          <table className="table mb-0" style={{ fontSize: "0.88rem" }}>
            <thead style={{ background: J.primary, color: "#fff" }}>
              <tr>
                <th style={{ padding: "12px 16px", fontWeight: 600 }}>#</th>
                <th style={{ padding: "12px 16px", fontWeight: 600 }}>Account Group</th>
                <th style={{ padding: "12px 16px", fontWeight: 600 }}>Base Group</th>
                <th style={{ padding: "12px 16px", fontWeight: 600 }}>Default</th>
                <th style={{ padding: "12px 16px", fontWeight: 600 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} className="text-center py-6"><LogoLoader size="md" /></td></tr>
              ) : paginated.length === 0 ? (
                <tr><td colSpan={5} className="text-center py-5 text-muted">No account groups found. Click <strong>+ Add</strong> to create one.</td></tr>
              ) : paginated.map((item, idx) => (
                <tr key={item.id || idx} style={{ borderBottom: "1px solid #f0f0f0" }}>
                  <td style={{ padding: "12px 16px", color: "#999", fontSize: "0.82rem" }}>{(currentPage - 1) * rowsPerPage + idx + 1}</td>
                  <td style={{ padding: "12px 16px", fontWeight: 600, color: J.text }}>{item.account_group}</td>
                  <td style={{ padding: "12px 16px" }}>
                    <span style={{ background: baseGroupColor[item.base_group] || J.primary, color: "#fff", borderRadius: 6, padding: "3px 10px", fontSize: "0.78rem", fontWeight: 600 }}>
                      {item.base_group}
                    </span>
                  </td>
                  <td style={{ padding: "12px 16px" }}>
                    <span style={{ background: item.is_default ? "#e8f5e9" : "#fafafa", color: item.is_default ? J.success : "#aaa", borderRadius: 6, padding: "3px 10px", fontSize: "0.78rem" }}>
                      {item.is_default ? "Yes" : "No"}
                    </span>
                  </td>
                  <td style={{ padding: "12px 16px" }}>
                    <button onClick={() => openEdit(item)} style={{ background: J.lightBg, border: `1px solid ${J.primary}`, color: J.primary, borderRadius: 6, padding: "4px 12px", cursor: "pointer", fontSize: "0.82rem" }}>
                      <i className="fas fa-edit" /> Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* ── Pagination ── */}
        <div style={{ padding: "6px 16px", borderTop: "1px solid #f0f0f0", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 6 }}>
          <div className="d-flex align-items-center gap-2 flex-nowrap" style={{ whiteSpace: "nowrap" }}>
            <span style={{ fontSize: "0.82rem", color: "#888", whiteSpace: "nowrap" }}>Rows per page:</span>
            <select value={rowsPerPage} onChange={e => { setRowsPerPage(Number(e.target.value)); setCurrentPage(1); }}
              style={{ border: `1px solid ${J.light}`, borderRadius: 6, padding: "2px 6px", height: 28, fontSize: "0.82rem", outline: "none" }}>
              {[5, 10, 25, 50].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          <div className="d-flex gap-1 flex-nowrap">
            {["First", "Prev", "Next", "Last"].map((lbl, i) => {
              const disabled = i < 2 ? currentPage === 1 : currentPage === totalPages;
              const onClick  = [() => setCurrentPage(1), () => setCurrentPage(p => Math.max(1, p - 1)), () => setCurrentPage(p => Math.min(totalPages, p + 1)), () => setCurrentPage(totalPages)][i];
              return <button key={lbl} onClick={onClick} disabled={disabled} style={{ border: `1px solid ${disabled ? "#ddd" : J.primary}`, background: lbl === "Next" ? J.primary : "#fff", color: lbl === "Next" ? "#fff" : disabled ? "#ccc" : J.primary, borderRadius: 6, padding: "3px 10px", height: 28, fontSize: "0.82rem", cursor: disabled ? "not-allowed" : "pointer" }}>{lbl}</button>;
            })}
          </div>
        </div>
      </div>

      {/* ── Modal ── */}
      {showModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1050, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <div style={{ background: "#fff", borderRadius: 14, width: "100%", maxWidth: 480, boxShadow: "0 20px 60px rgba(0,0,0,0.2)", overflow: "hidden" }}>
            <div style={{ background: `linear-gradient(135deg, ${J.primary}, ${J.accent})`, padding: "16px 20px", color: "#fff", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <h6 className="mb-0 fw-bold">{editingItem ? "Edit" : "Add"} Account Group</h6>
              <button onClick={closeModal} style={{ background: "rgba(255,255,255,0.2)", border: "none", color: "#fff", borderRadius: 6, width: 28, height: 28, cursor: "pointer", fontSize: "0.9rem" }}>✕</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div style={{ padding: "20px" }}>
                <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
                  <button type="button" onClick={() => setFormData({ base_group: "", account_group: "" })}
                    style={{ background: "none", border: "none", color: J.danger, cursor: "pointer", fontSize: "0.82rem" }}>Clear</button>
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: "block", fontWeight: 600, marginBottom: 6, fontSize: "0.88rem", color: J.text }}>Base Group</label>
                  <select value={formData.base_group} onChange={e => setFormData(p => ({ ...p, base_group: e.target.value }))} required
                    style={{ width: "100%", border: `1px solid ${J.light}`, borderRadius: 8, padding: "9px 12px", fontSize: "0.88rem", outline: "none" }}>
                    <option value="">-- Select Base Group --</option>
                    {BASE_GROUP_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: "block", fontWeight: 600, marginBottom: 6, fontSize: "0.88rem", color: J.text }}>Account Group Name</label>
                  <input type="text" value={formData.account_group} onChange={e => setFormData(p => ({ ...p, account_group: e.target.value }))} required placeholder="e.g. Cash in Hand, Bank Accounts"
                    style={{ width: "100%", border: `1px solid ${J.light}`, borderRadius: 8, padding: "9px 12px", fontSize: "0.88rem", outline: "none", boxSizing: "border-box" }} />
                </div>
              </div>
              <div style={{ padding: "12px 20px", borderTop: "1px solid #f0f0f0", display: "flex", justifyContent: "center", gap: 10 }}>
                <button type="button" onClick={closeModal} style={{ border: `1px solid #ddd`, background: "#fff", color: "#666", borderRadius: 8, padding: "8px 24px", cursor: "pointer" }}>Cancel</button>
                <button type="submit" disabled={loading} style={{ background: `linear-gradient(135deg, ${J.primary}, ${J.accent})`, border: "none", color: "#fff", borderRadius: 8, padding: "8px 32px", fontWeight: 700, cursor: loading ? "not-allowed" : "pointer" }}>
                  {loading ? "Saving..." : "Submit"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountGroup;
